import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Save } from 'lucide-react';
import { trpc } from '../lib/trpc';
import { translateError } from '../lib/errors';
import { useLocale } from '../contexts/LocaleContext';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  Input,
  Label,
  PageSpinner,
} from '../components/ui';

export function FormsPage() {
  const { t } = useLocale();
  const utils = trpc.useUtils();
  const list = trpc.facebookForms.list.useQuery();
  const setStreamId = trpc.facebookForms.setStreamId.useMutation({
    onSuccess: () => {
      toast.success(t('forms.toastSaved'));
      void utils.facebookForms.list.invalidate();
    },
    onError: (e) => toast.error(translateError(e)),
  });

  const [drafts, setDrafts] = useState<Record<number, string>>({});
  useEffect(() => {
    if (!list.data) return;
    setDrafts((prev) => {
      const next: Record<number, string> = { ...prev };
      for (const f of list.data) {
        if (next[f.id] === undefined) next[f.id] = f.streamId ?? '';
      }
      return next;
    });
  }, [list.data]);

  const grouped = useMemo(() => {
    const by = new Map<string, NonNullable<typeof list.data>>();
    for (const f of list.data ?? []) {
      const k = f.pageName;
      const arr = by.get(k) ?? [];
      arr.push(f);
      by.set(k, arr);
    }
    return Array.from(by.entries());
  }, [list.data]);

  if (list.isLoading) return <PageSpinner />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{t('forms.title')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t('forms.subtitle')}</p>
      </div>

      {(list.data?.length ?? 0) === 0 ? (
        <EmptyState title={t('forms.emptyTitle')} description={t('forms.emptyDescription')} />
      ) : (
        <div className="space-y-4">
          {grouped.map(([pageName, forms]) => (
            <Card key={pageName}>
              <CardHeader>
                <CardTitle>{pageName}</CardTitle>
                <CardDescription>
                  {forms.length === 1 ? t('forms.formCountOne') : t('forms.formCount', { count: forms.length })}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {forms.map((f) => {
                  const draft = drafts[f.id] ?? '';
                  const current = f.streamId ?? '';
                  const dirty = draft !== current;
                  return (
                    <div
                      key={f.id}
                      className="flex flex-col gap-2 rounded-md border p-3 sm:flex-row sm:items-end sm:gap-3"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="truncate font-medium">{f.name}</span>
                          {f.streamId ? (
                            <Badge variant="success">{t('forms.connected')}</Badge>
                          ) : (
                            <Badge variant="warning">{t('forms.noStreamId')}</Badge>
                          )}
                        </div>
                        <p className="mt-0.5 truncate text-xs text-muted-foreground">
                          {t('forms.formId', { id: f.formId })}
                        </p>
                      </div>
                      <div className="w-full sm:w-64">
                        <Label htmlFor={`stream-${f.id}`} className="text-xs">
                          {t('forms.streamId')}
                        </Label>
                        <Input
                          id={`stream-${f.id}`}
                          value={draft}
                          placeholder={t('forms.streamIdPlaceholder')}
                          onChange={(e) => setDrafts((d) => ({ ...d, [f.id]: e.target.value }))}
                        />
                      </div>
                      <Button
                        size="sm"
                        disabled={!dirty || setSaving(setStreamId, f.id)}
                        onClick={() =>
                          setStreamId.mutate({
                            id: f.id,
                            streamId: draft.trim() ? draft.trim() : null,
                          })
                        }
                      >
                        <Save className="h-3.5 w-3.5" /> {t('forms.save')}
                      </Button>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function setSaving(m: { isPending: boolean; variables?: { id: number } }, id: number): boolean {
  return m.isPending && m.variables?.id === id;
}
